import React, {
    ChangeEvent,
    Fragment,
    ReactNode,
    SetStateAction,
    useState
} from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import Form, {
    ErrorMessage,
    Field,
    FormFooter,
    FormSection,
    HelperMessage
} from "@atlaskit/form";
import ChevronRightIcon from "@atlaskit/icon/core/chevron-right";
import TextField from "@atlaskit/textfield";

import { Button } from "@/components/wpl/button/src/button";
import { Link } from "@/libraries/design/components";
import { useLocalizedHref } from "@/libraries/web-framework/src/localization/use-localized-href";
import { DEFAULT_VALUES } from "@/libraries/web-framework/src/localization/values";
import { Row } from "@/libraries/wpl/foundations/src/grid";

import { useLoggedInUser } from "../../../../../utils/logged-in-user-provider";
import { ConsentInfo } from "../components/consent-info";
import { NumberOfUsersField } from "../components/number-of-users-dropdown";
import { SelectedProductProvider } from "../components/product-selector";
import { QuestionField } from "../components/your-question";
import personalEmailsDomains from "../personal-emails-domains";
import { isEmailFormat, submitContactForm } from "../utils";

import MigrationOptionsResourceCards from "./mo-resource-cards";

interface SelectorProps {
    onChangeHandler?: React.Dispatch<SetStateAction<string>>;
}

const dropdownSelectorStyle = css({
    "alignItems": "center",
    "boxSizing": "border-box",
    "display": "block",
    "padding": "11px 56px 12px 12px",
    "minHeight": "48px",
    "overflowX": "visible",
    "overflowY": "visible",
    "width": "100%",
    "fontFamily": `"Charlie Text", -apple-system, "system-ui", "Segoe UI", Roboto, "Noto Sans", Ubuntu, "Droid Sans", "Helvetica Neue", sans-serif`,
    "fontFeatureSettings": "normal",
    "fontKerning": "auto",
    "fontOpticalSizing": "auto",
    "fontSize": "16px",
    "fontStretch": "100%",
    "fontStyle": "normal",
    "fontWeight": "400",
    "letterSpacing": "normal",
    "lineHeight": "1.2",
    "textAlign": "left",
    "textIndent": "0px",
    "textShadow": "none",
    "textTransform": "none",
    "textWrap": "nowrap",
    "whiteSpaceCollapse": "collapse",
    "wordSpacing": "normal",
    "color": "#253858",
    "backgroundColor": "#f4f5f7",
    "borderRadius": "5px",
    "backgroundImage": `linear-gradient(45deg, rgba(0, 0, 0, 0) 40%, rgb(37, 56, 88) 40%, rgb(37, 56, 88) 60%, rgba(0, 0, 0, 0) 60%), linear-gradient(-45deg, rgba(0, 0, 0, 0) 40%, rgb(37, 56, 88) 40%, rgb(37, 56, 88) 60%, rgba(0, 0, 0, 0) 60%)`,
    "backgroundPositionX": `calc(100% - 28px), calc(100% - 22px)`,
    "backgroundPositionY": "50%, 50%",
    "backgroundRepeat": "no-repeat, no-repeat",
    "backgroundSize": "8px 8px, 8px 8px",
    "borderImageOutset": "0",
    "borderImageRepeat": "stretch",
    "borderImageSlice": "100%",
    "border": "none",
    "cursor": "pointer",
    "WebkitAppearance": "none",
    ":hover": {
        // eslint-disable-next-line local/ensure-wac-token-usage
        backgroundColor: "#deebff"
    },
    ":focus": {
        outline: "none",
        border: "1px",
        borderStyle: "solid",
        // eslint-disable-next-line local/ensure-wac-token-usage
        borderColor: "#0049b0"
    },
    ":focus-visible": {
        outline: "none",
        border: "1px",
        borderStyle: "solid",
        // eslint-disable-next-line local/ensure-wac-token-usage
        borderColor: "#0049b0"
    }
});

const MigratingFromDropdown = ({ onChangeHandler }: SelectorProps) => {
    const [selectedOption, setSelectedOption] = useState("");

    const onClick = (event: ChangeEvent<HTMLSelectElement>) => {
        const selected = String(event.target.value);
        setSelectedOption(selected);
        onChangeHandler?.(selected);
    };
    const intl = useIntl();
    const serverToCloud = intl.formatMessage({
        id: "migrationOptions.serverToCloud",
        defaultMessage: "Server to Cloud"
    });

    const serverToDataCenter = intl.formatMessage({
        id: "migrationOptions.serverToDataCenter",
        defaultMessage: "Server to Data Center"
    });

    const dataCenterToCloud = intl.formatMessage({
        id: "migrationOptions.dataCenterToCloud",
        defaultMessage: "Data Center to Cloud"
    });

    const iDontKnowYet = intl.formatMessage({
        id: "migrationOptions.iDontKnowYet",
        defaultMessage: "I don't know yet"
    });

    return (
        <select
            css={dropdownSelectorStyle}
            id="options"
            value={selectedOption}
            onChange={onClick}
            data-testid="migration-options"
        >
            <option value="" />
            <option value="Server to cloud">{serverToCloud}</option>
            <option value="Server to Data Center">{serverToDataCenter}</option>
            <option value="Data Center to Cloud">{dataCenterToCloud}</option>
            <option value="I don’t know">{iDontKnowYet}</option>
        </select>
    );
};

const LearnAboutMigrationsDropdown = ({ onChangeHandler }: SelectorProps) => {
    const [selectedOption, setSelectedOption] = useState("");

    const onClick = (event: ChangeEvent<HTMLSelectElement>) => {
        const selected = String(event.target.value);
        setSelectedOption(selected);
        onChangeHandler?.(selected);
    };

    const intl = useIntl();

    const pricingAndQuotes = intl.formatMessage({
        id: "migrationOptions.pricingAndQuotes",
        defaultMessage: "Pricing and quotes"
    });
    const securityAndCompliance = intl.formatMessage({
        id: "migrationOptions.securityAndCompliance",
        defaultMessage: "Security and compliance"
    });

    const productFeatures = intl.formatMessage({
        id: "migrationOptions.productFeatures",
        defaultMessage: "Product features"
    });

    return (
        <select
            css={dropdownSelectorStyle}
            id="options"
            value={selectedOption}
            onChange={onClick}
            data-testid="migration-learn-options"
        >
            <option value="" />
            <option value="Pricing and quotes">{pricingAndQuotes}</option>
            <option value="Security and compliance">
                {securityAndCompliance}
            </option>
            <option value="Product features">{productFeatures}</option>
        </select>
    );
};

const containerStyle = css({
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#42526e",
    boxSizing: "border-box",
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    padding: "16px",
    fontFamily:
        "Charlie Text, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Noto Sans, Ubuntu, Droid Sans, Helvetica Neue, sans-serif",
    fontWeight: "500",
    lineHeight: "24px"
});

const innerContainerStyle = css({
    alignContent: "flex-start",
    boxSizing: "border-box",
    display: "flex",
    flexBasis: "0%",
    flexGrow: "2",
    flexShrink: "1",
    flexWrap: "wrap",
    minWidth: "50%",
    maxWidth: "620px"
});

// tech debt that should be addressed separately
const formStyle = css({
    "width": "100%",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "[class*=-Field]": {
        fontFamily:
            "'Charlie Text', -apple-system, 'system-ui', 'Segoe UI', Roboto, 'Noto Sans', Ubuntu, 'Droid Sans', 'Helvetica Neue', sans-serif",
        fontSize: "1rem",
        lineHeight: "2.5rem",
        // eslint-disable-next-line local/ensure-wac-token-usage
        color: "#de350b",
        padding: "8px 0 8px"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "[for*=uid]": {
        fontWeight: "500",
        fontSize: "1.25rem",
        fontFamily:
            "'Charlie Text', -apple-system, 'system-ui', 'Segoe UI', Roboto, 'Noto Sans', Ubuntu, 'Droid Sans', 'Helvetica Neue', sans-serif"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "[id*=error]": {
        "fontFamily":
            "'Charlie Text', -apple-system, 'system-ui', 'Segoe UI', Roboto, 'Noto Sans', Ubuntu, 'Droid Sans', 'Helvetica Neue', sans-serif",
        "fontSize": "1rem",
        "color": "#DE350B",
        "lineHeight": "24px",
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "& > span": {
            display: "none"
        }
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "[id*=helper]": {
        fontFamily:
            "'Charlie Text', -apple-system, 'system-ui', 'Segoe UI', Roboto, 'Noto Sans', Ubuntu, 'Droid Sans', 'Helvetica Neue', sans-serif",
        fontSize: "1rem"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "[type=number]": {
        fontSize: "1rem"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".row-layout": {
        marginBlockStart: "24px",
        paddingInlineStart: 0,
        paddingInlineEnd: 0
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "[role=presentation]": {
        "backgroundColor": "#f4f5f7",
        "borderColor": "transparent",
        "borderRadius": "5px",
        "lineHeight": "2.5rem",
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "& > input": {
            fontFamily:
                "'Charlie Text', -apple-system, 'system-ui', 'Segoe UI', Roboto, 'Noto Sans', Ubuntu, 'Droid Sans', 'Helvetica Neue', sans-serif",
            fontWeight: "400",
            fontSize: "1.1rem",
            lineHeight: "2.5rem"
        }
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "[name=opportunity_Description__c]": {
        // eslint-disable-next-line local/ensure-wac-token-usage
        backgroundColor: "#f4f5f7 !important",
        borderColor: "transparent !important",
        fontSize: "1.1rem",
        lineHeight: "2rem",
        borderRadius: "5px",
        resize: "both"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".product-selector": {
        "paddingInlineStart": 0,
        "paddingInlineEnd": 0,
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "button": {
            // eslint-disable-next-line local/ensure-wac-token-usage
            backgroundColor: "#f4f5f7",
            borderColor: "transparent",
            lineHeight: "2rem",
            fontSize: "1.5rem"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "& > div": {
            // eslint-disable-next-line local/ensure-wac-token-usage
            boxShadow: "1px 2px 5px rgba(5,3,3,.4392156863)",
            zIndex: "2", // so it doesn't overlap the header with z-index: 3
            transition: "max-height .4s",
            overflow: "hidden",
            opacity: "1",
            width: "99%"
        }
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".post-submit": {
        display: "none"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".post-submit-error": {
        display: "block",
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        p: {
            // eslint-disable-next-line local/ensure-wac-token-usage
            color: "#de350b"
        }
    }
});

const textContainer = css({
    marginBlockStart: "5px",
    marginBlockEnd: "3px",
    fontSize: "20px",
    fontWeight: "400",
    letterSpacing: "0.3px",
    lineHeight: "28px",
    verticalAlign: "baseline",
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#42526e"
});

const MigrationOptions = () => {
    const intl = useIntl();
    const userInfo = useLoggedInUser();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const [fieldHasError, setFieldHasError] = useState<{
        [x: string]: boolean;
    }>({});
    const [errorMessageText, setErrorMessageText] = useState("");
    const [selectedProduct] = useState("");
    const [isInvalid, setIsInvalid] = useState(true);
    const [showProductField, setShowProductField] = useState(false);
    const [changeContactInfo, setChangeContactInfo] = useState(false);
    const [migrationDirection, setMigrationDirection] = useState("");

    const NEXT_PUBLIC_IDENTITY_BASE_URL =
        process.env["NEXT_PUBLIC_IDENTITY_BASE_URL"];
    const getSwitchAccountURL = (continueUrlParam?: string) => {
        const continueUrl = continueUrlParam
            ? continueUrlParam
            : window.location.href;
        return `${NEXT_PUBLIC_IDENTITY_BASE_URL}/login/select-account?continue=${encodeURIComponent(
            continueUrl
        )}`;
    };

    /* 
       Prompts
    */
    const yourCompanyEmailPrompt = intl.formatMessage({
        id: "migrationOptions.yourCompanyEmail",
        defaultMessage: "Your company email"
    });

    const personalEmailDomainsErrorPrompt = intl.formatMessage({
        id: "migrationOptions.personalEmailDomainsPrompt",
        defaultMessage: "Personal email domains will result in an error."
    });

    const learnAboutMigrationsPrompt = intl.formatMessage({
        id: "migrationOptions.learnAboutMigrationsPrompt",
        defaultMessage: "This sets some context for our conversation."
    });

    /*
        Error Messages
    */

    const firstNameErrorMessage = intl.formatMessage({
        id: "migrationOptions.firstNameErrorMessage",
        defaultMessage: "Your first name is required."
    });

    const lastNameErrorMessage = intl.formatMessage({
        id: "migrationOptions.lastNameErrorMessage",
        defaultMessage: "Your last name is required."
    });

    const companyErrorMessage = intl.formatMessage({
        id: "migrationOptions.companyErrorMessage",
        defaultMessage: "Your company name is required."
    });

    const jobTitleErrorMessage = intl.formatMessage({
        id: "migrationOptions.jobTitleErrorMessage",
        defaultMessage: "Your job title is required."
    });

    const migratingFromErrorMessage = intl.formatMessage({
        id: "migrationOptions.migratingFromErrorMessage",
        defaultMessage: "Select a migration path to continue."
    });

    const productErrorMessage = intl.formatMessage({
        id: "migrationOptions.productErrorMessage",
        defaultMessage: "Select a product or service to continue."
    });

    const notEmailErrorMessage = intl.formatMessage({
        id: "migrationOptions.notEmailErrorMessage",
        defaultMessage: "Enter a valid email address."
    });

    const personalDomainErrorMessage = intl.formatMessage({
        id: "migrationOptions.personalDomainErrorMessage",
        defaultMessage: "Enter an email with a company domain."
    });

    const whatDoYouWantToLearnErrorMessage = intl.formatMessage({
        id: "migrationOptions.whatDoYouWantToLearnErrorMessage",
        defaultMessage: "Select a topic to continue."
    });

    const yourQuestionErrorMessage = intl.formatMessage({
        id: "migrationOptions.yourQuestionErrorMessage",
        defaultMessage: "Enter a quick description of your question."
    });

    const userCountGreaterThan5000Error = intl.formatMessage({
        id: "migrationOptions.userCountErrorMessage",
        defaultMessage: "Enter a number that’s less than 5,000,000."
    });

    const companyNameLengthError = intl.formatMessage({
        id: "migrationOptions.companyNameLengthErrorMessage",
        defaultMessage: "Enter a company name that’s at least two characters."
    });

    const isPersonalDomain = (value: string) => {
        const emailDomain = value.split("@")[1];
        return personalEmailsDomains.has(String(emailDomain));
    };

    // @ts-ignore
    const handleBlurEvent = (event) => {
        const { name, value } = event.currentTarget;

        if (name === "email") {
            if (!value) {
                setFieldHasError({ ...fieldHasError, [name]: true });
                setErrorMessageText("NOT_EMAIL");
                setIsInvalid(true);
            } else {
                if (isEmailFormat(value) && !isPersonalDomain(value)) {
                    setFieldHasError({ ...fieldHasError, [name]: false });
                    setErrorMessageText("IS_VALID");
                    setIsInvalid(false);
                } else {
                    setFieldHasError({ ...fieldHasError, [name]: true });
                    setIsInvalid(true);
                    if (!isEmailFormat(value)) {
                        setErrorMessageText("NOT_EMAIL");
                    } else if (isPersonalDomain(value)) {
                        setErrorMessageText("PERSONAL_DOMAIN");
                    }
                }
            }
        } else {
            setFieldHasError({ ...fieldHasError, [name]: !value });
            setIsInvalid(!value);
        }
    };

    // eslint-disable-next-line complexity
    const handleSubmit = (form: HTMLFormElement) => {
        const errors: { [field: string]: string } = {};
        // eslint-disable-next-line @typescript-eslint/no-for-in-array
        for (const field in form) {
            if (form[field] === undefined || fieldHasError[field]) {
                switch (field) {
                    case "email":
                        if (fieldHasError[field]) {
                            if (isPersonalDomain(form[field] as string)) {
                                errors["email"] = personalDomainErrorMessage;
                            }
                        }
                        errors["email"] = notEmailErrorMessage;
                        break;
                    case "firstName":
                        errors["firstName"] = firstNameErrorMessage;
                        break;
                    case "lastName":
                        errors["lastName"] = lastNameErrorMessage;
                        break;
                    case "company":
                        errors["company"] = companyErrorMessage;
                        break;
                    case "title":
                        errors["title"] = jobTitleErrorMessage;
                        break;
                    case "migrationPath":
                        errors["migrationPath"] = migratingFromErrorMessage;
                        break;
                    case "product":
                        errors["product"] = productErrorMessage;
                        break;
                    case "numberOfUsers":
                        errors["numberOfUsers"] =
                            numberOfUsersProductErrorMessage;
                        break;
                    case "formChoices":
                        errors["formChoices"] =
                            whatDoYouWantToLearnErrorMessage;
                        break;
                    case "opportunity_Description__c":
                        errors["opportunity_Description__c"] =
                            yourQuestionErrorMessage;
                        break;
                }
            }
        }

        if (Object.keys(errors).length > 0) {
            return errors;
        } else {
            return submitContactForm(
                form,
                "migration-options",
                createAnalyticsEvent
            )
                .then(() => {
                    window.location.assign(redirectPath);
                })
                .catch(() => {
                    document
                        .querySelector(".post-submit")
                        ?.classList.replace("post-submit", "post-submit-error");
                });
        }
    };

    const localizedContactLink = useLocalizedHref("/contact");

    const redirectPath = useLocalizedHref(
        `/enterprise/contact-thanks?formType=migration-options`
    );

    const SubTextSection = () => (
        <div css={textContainer}>
            <p>
                <FormattedMessage
                    id="migrationOptions.formBestFitMigrationPathSubText"
                    defaultMessage={
                        "We can help you assess which migration path is the best fit for your Server or Data Center licenses."
                    }
                />
            </p>
            <p>
                <FormattedMessage
                    id="migrationOptions.decidedToAlreadyMigrateSubText"
                    defaultMessage={
                        "<span>If you've already decided to migrate, please <raiseASupportTicketLink>raise a support ticket</raiseASupportTicketLink> for help with migration planning and execution.</span>"
                    }
                    values={{
                        ...DEFAULT_VALUES,
                        raiseASupportTicketLink: (text: ReactNode) => (
                            <Link
                                aria-label={text as unknown as string}
                                text={text as unknown as string}
                                href={localizedContactLink}
                                type={"inline"}
                            />
                        )
                    }}
                />
            </p>
        </div>
    );

    const MigrationFromField = (
        setFieldValue: (name: string, value: unknown) => void
    ) => {
        return (
            <div css={css({ paddingBlockStart: "8px" })}>
                <Field
                    name={"migrationPath"}
                    label={
                        intl.formatMessage({
                            id: "migrationOptions.migratingFrom",
                            defaultMessage: "Where are you migrating from?"
                        }) + " *"
                    }
                >
                    {({ fieldProps, error }) => (
                        <Fragment>
                            <MigratingFromDropdown
                                {...fieldProps}
                                onChangeHandler={(migrationPath) => {
                                    setFieldValue(
                                        "migrationPath",
                                        migrationPath
                                    );
                                    setShowProductField(true);
                                    setMigrationDirection(migrationPath);
                                    fieldProps.onChange;
                                }}
                            />
                            {error && (
                                <ErrorMessage>
                                    {migratingFromErrorMessage}
                                </ErrorMessage>
                            )}
                        </Fragment>
                    )}
                </Field>
            </div>
        );
    };

    const ProductSelectorDropdown = ({ onChangeHandler }: SelectorProps) => {
        const [selectedOption, setSelectedOption] = useState("");

        const onClick = (event: ChangeEvent<HTMLSelectElement>) => {
            const selected = String(event.target.value);
            setSelectedOption(selected);
            onChangeHandler?.(selected);
        };
        const intl = useIntl();
        const jiraSoftwareOption = intl.formatMessage({
            id:
                migrationDirection !== "Server to Data Center"
                    ? "migrationOptions.jira"
                    : "migrationOptions.jiraSoftware",
            defaultMessage:
                migrationDirection !== "Server to Data Center"
                    ? "Jira"
                    : "Jira Software"
        });

        const confluenceOption = intl.formatMessage({
            id: "migrationOptions.confluence",
            defaultMessage: "Confluence"
        });

        const bitbucketOption = intl.formatMessage({
            id: "migrationOptions.bitbucket",
            defaultMessage: "Bitbucket"
        });

        const crowdOption = intl.formatMessage({
            id: "migrationOptions.crowd",
            defaultMessage: "Crowd"
        });

        const jsmOption = intl.formatMessage({
            id: "migrationOptions.jsm",
            defaultMessage: "Jira Service Management"
        });

        return (
            <select
                css={dropdownSelectorStyle}
                id="options"
                value={selectedOption}
                onChange={onClick}
                data-testid="migration-options-product-dropdown"
            >
                <option value="" />
                <option value={jiraSoftwareOption}>{jiraSoftwareOption}</option>
                <option value="Confluence">{confluenceOption}</option>
                <option value="Bitbucket">{bitbucketOption}</option>
                <option value="Crowd">{crowdOption}</option>
                <option value="Jira Service Management">{jsmOption}</option>
            </select>
        );
    };

    const ProductField = (
        setFieldValue: (name: string, value: unknown) => void
    ) => {
        return (
            showProductField && (
                <div css={css({ paddingBlockStart: "36px" })}>
                    <Field
                        name={"product"}
                        label={
                            intl.formatMessage({
                                id: "migrationOptions.product",
                                defaultMessage:
                                    "Which product are you inquiring about?"
                            }) + " *"
                        }
                    >
                        {({ fieldProps, error }) => (
                            <Fragment>
                                <ProductSelectorDropdown
                                    {...fieldProps}
                                    onChangeHandler={(product) => {
                                        setFieldValue("product", product);
                                        fieldProps.onChange;
                                    }}
                                />
                                {(error && (
                                    <ErrorMessage>
                                        {productErrorMessage}
                                    </ErrorMessage>
                                )) || (
                                    <HelperMessage>
                                        {intl.formatMessage({
                                            id: "migrationOptions.productHelper",
                                            defaultMessage:
                                                "If you want to migrate multiple products, select the one with the most users."
                                        })}
                                    </HelperMessage>
                                )}
                            </Fragment>
                        )}
                    </Field>
                </div>
            )
        );
    };

    const numberOfUsersProductErrorMessage = useIntl().formatMessage({
        id: "migrationOptions.numberOfUsersProductErrorMessage",
        defaultMessage: "Enter the number of users for this product."
    });

    const WhatDoYouWantToLearnField = (
        setFieldValue: (name: string, value: unknown) => void
    ) => (
        <div
            css={css({
                paddingBlockStart: "36px",
                paddingBlockEnd: "36px"
            })}
        >
            <Field
                name={"formChoices"}
                label={
                    intl.formatMessage({
                        id: "migrationOptions.whatDoYouWantToLearn",
                        defaultMessage:
                            "What do you want to learn about migrations?"
                    }) + " *"
                }
            >
                {({ fieldProps, error }) => (
                    <Fragment>
                        <LearnAboutMigrationsDropdown
                            {...fieldProps}
                            onChangeHandler={(formChoice) => {
                                setFieldValue("formChoices", formChoice);
                                fieldProps.onChange;
                            }}
                        />
                        {(error && (
                            <ErrorMessage>
                                {whatDoYouWantToLearnErrorMessage}
                            </ErrorMessage>
                        )) || (
                            <HelperMessage>
                                {learnAboutMigrationsPrompt}
                            </HelperMessage>
                        )}
                    </Fragment>
                )}
            </Field>
        </div>
    );

    const FooterSection = (
        onSubmit: (
            event?:
                | React.FormEvent<HTMLFormElement>
                | React.SyntheticEvent<HTMLElement>
        ) => void
    ) => (
        <Fragment>
            <div>
                <Button
                    inputType={"button"}
                    text={intl.formatMessage({
                        id: "migrationOptions.submit",
                        defaultMessage: "Submit"
                    })}
                    type="primary"
                    color={isInvalid ? "gray" : "darkblue"}
                    onClick={() => {
                        onSubmit();
                    }}
                />
            </div>
            <div className={"post-submit"}>
                <p>
                    {intl.formatMessage({
                        id: "migrationOptions.submitError",
                        defaultMessage:
                            "An error has occurred. Please try again later."
                    })}
                </p>
            </div>
        </Fragment>
    );

    const loggedOutView = (
        <SelectedProductProvider selectedProduct={selectedProduct}>
            <div css={containerStyle}>
                <div css={innerContainerStyle}>
                    <SubTextSection />
                    <Form onSubmit={handleSubmit}>
                        {({ formProps, setFieldValue }) => (
                            <form {...formProps} css={formStyle}>
                                <FormSection>
                                    <Field
                                        name="email"
                                        label={yourCompanyEmailPrompt + " *"}
                                        validate={(value) => {
                                            setFieldValue("email", value);
                                        }}
                                    >
                                        {({ fieldProps, error }) => (
                                            <Fragment>
                                                <TextField
                                                    {...fieldProps}
                                                    value={
                                                        fieldProps.value || ""
                                                    }
                                                    onBlur={handleBlurEvent}
                                                />
                                                {(error && (
                                                    <ErrorMessage>
                                                        {errorMessageText ===
                                                        "NOT_EMAIL"
                                                            ? notEmailErrorMessage
                                                            : errorMessageText ===
                                                              "PERSONAL_DOMAIN"
                                                            ? personalDomainErrorMessage
                                                            : error}
                                                    </ErrorMessage>
                                                )) || (
                                                    <HelperMessage>
                                                        {
                                                            personalEmailDomainsErrorPrompt
                                                        }
                                                    </HelperMessage>
                                                )}
                                            </Fragment>
                                        )}
                                    </Field>
                                </FormSection>
                                <FormSection>
                                    <Row layout={"6-6"}>
                                        <Field
                                            name={"firstName"}
                                            label={
                                                intl.formatMessage({
                                                    id: "migrationOptions.forename",
                                                    defaultMessage: "First name"
                                                }) + " *"
                                            }
                                        >
                                            {({ fieldProps, error }) => (
                                                <Fragment>
                                                    <TextField
                                                        {...fieldProps}
                                                        value={
                                                            fieldProps.value ||
                                                            ""
                                                        }
                                                        onBlur={handleBlurEvent}
                                                    />
                                                    {error && (
                                                        <ErrorMessage>
                                                            {
                                                                firstNameErrorMessage
                                                            }
                                                        </ErrorMessage>
                                                    )}
                                                </Fragment>
                                            )}
                                        </Field>
                                        <Field
                                            name={"lastName"}
                                            label={
                                                intl.formatMessage({
                                                    id: "migrationOptions.surname",
                                                    defaultMessage: "Last name"
                                                }) + " *"
                                            }
                                        >
                                            {({ fieldProps, error }) => (
                                                <Fragment>
                                                    <TextField
                                                        {...fieldProps}
                                                        value={
                                                            fieldProps.value ||
                                                            ""
                                                        }
                                                        onBlur={handleBlurEvent}
                                                    />
                                                    {error && (
                                                        <ErrorMessage>
                                                            {
                                                                lastNameErrorMessage
                                                            }
                                                        </ErrorMessage>
                                                    )}
                                                </Fragment>
                                            )}
                                        </Field>
                                    </Row>
                                    <Row layout={"6-6"}>
                                        <Field
                                            name={"company"}
                                            label={
                                                intl.formatMessage({
                                                    id: "migrationOptions.company",
                                                    defaultMessage: "Company"
                                                }) + " *"
                                            }
                                        >
                                            {({ fieldProps, error }) => (
                                                <Fragment>
                                                    <TextField
                                                        {...fieldProps}
                                                        value={
                                                            fieldProps.value ||
                                                            ""
                                                        }
                                                        onBlur={handleBlurEvent}
                                                    />
                                                    {error && (
                                                        <ErrorMessage>
                                                            {
                                                                companyErrorMessage
                                                            }
                                                        </ErrorMessage>
                                                    )}
                                                </Fragment>
                                            )}
                                        </Field>
                                        <Field
                                            name={"title"}
                                            label={
                                                intl.formatMessage({
                                                    id: "migrationOptions.title",
                                                    defaultMessage: "Job title"
                                                }) + " *"
                                            }
                                        >
                                            {({ fieldProps, error }) => (
                                                <Fragment>
                                                    <TextField
                                                        {...fieldProps}
                                                        value={
                                                            fieldProps.value ||
                                                            ""
                                                        }
                                                        onBlur={handleBlurEvent}
                                                    />
                                                    {error && (
                                                        <ErrorMessage>
                                                            {
                                                                jobTitleErrorMessage
                                                            }
                                                        </ErrorMessage>
                                                    )}
                                                </Fragment>
                                            )}
                                        </Field>
                                    </Row>
                                </FormSection>
                                <FormSection>
                                    {/* Issue with infinite rendering when using component syntax, so calling as JS function. (e.g. <MigrationFromField {...formProps} />) */}
                                    {MigrationFromField(setFieldValue)}
                                    {ProductField(setFieldValue)}
                                    {NumberOfUsersField(setFieldValue)}
                                    {WhatDoYouWantToLearnField(setFieldValue)}
                                    {QuestionField(
                                        "migration-options",
                                        handleBlurEvent
                                    )}
                                </FormSection>
                                {ConsentInfo(setFieldValue)}
                                <FormFooter align={"start"}>
                                    {FooterSection(formProps.onSubmit)}
                                </FormFooter>
                            </form>
                        )}
                    </Form>
                </div>
                <MigrationOptionsResourceCards />
            </div>
        </SelectedProductProvider>
    );

    const loggedInView = (
        <SelectedProductProvider selectedProduct={selectedProduct}>
            <div css={containerStyle}>
                <div css={innerContainerStyle}>
                    <SubTextSection />
                    <Form onSubmit={handleSubmit}>
                        {({ formProps, setFieldValue }) => (
                            <form {...formProps} css={formStyle}>
                                <p>
                                    {intl.formatMessage({
                                        id: "migrationOptions.contactInfo",
                                        defaultMessage: "You're logged in as:"
                                    })}
                                </p>
                                <>{setFieldValue("email", userInfo.email)}</>
                                <>{setFieldValue("name", userInfo.name)}</>
                                <p css={css({ fontSize: "1.25rem" })}>
                                    {userInfo.name}
                                    <br />
                                    {userInfo.email}
                                </p>
                                <div
                                    css={css({
                                        fontSize: "0.75rem",
                                        // eslint-disable-next-line local/ensure-wac-token-usage
                                        color: "#0052cc",
                                        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                                        a: {
                                            "textDecoration": "none",
                                            "color": "#0052cc",
                                            "display": "inline-block",
                                            "marginInlineEnd": "30px",
                                            "marginBlockEnd": "1rem",
                                            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                                            "[role=presentation]": {
                                                backgroundColor: "transparent"
                                            }
                                        }
                                    })}
                                >
                                    <a
                                        href="#changeContactInfo"
                                        onClick={() =>
                                            setChangeContactInfo(true)
                                        }
                                    >
                                        {intl.formatMessage({
                                            id: "migrationOptions.updateInfo",
                                            defaultMessage:
                                                "Update contact info"
                                        })}
                                        <span
                                            css={css({
                                                // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                                                "& > span": {
                                                    lineHeight: "unset"
                                                }
                                            })}
                                        >
                                            <ChevronRightIcon label="" />
                                        </span>
                                    </a>
                                    <a href={getSwitchAccountURL()}>
                                        {intl.formatMessage({
                                            id: "migrationOptions.differentAccount",
                                            defaultMessage:
                                                "Log in with a different Atlassian account"
                                        })}
                                        <span
                                            css={css({
                                                // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                                                "& > span": {
                                                    lineHeight: "unset"
                                                }
                                            })}
                                        >
                                            <ChevronRightIcon label="" />
                                        </span>
                                    </a>
                                </div>
                                <FormSection>
                                    <Field
                                        name={"title"}
                                        label={
                                            intl.formatMessage({
                                                id: "migrationOptions.yourJobTitle",
                                                defaultMessage: "Your job title"
                                            }) + " *"
                                        }
                                    >
                                        {({ fieldProps, error }) => (
                                            <Fragment>
                                                <TextField
                                                    {...fieldProps}
                                                    value={
                                                        fieldProps.value || ""
                                                    }
                                                    onBlur={handleBlurEvent}
                                                />
                                                {error && (
                                                    <ErrorMessage>
                                                        {jobTitleErrorMessage}
                                                    </ErrorMessage>
                                                )}
                                                <HelperMessage>
                                                    {intl.formatMessage({
                                                        id: "migrationOptions.yourJobTitleHelper",
                                                        defaultMessage:
                                                            "We ask this to connect you to the best expert for your role."
                                                    })}
                                                </HelperMessage>
                                            </Fragment>
                                        )}
                                    </Field>
                                </FormSection>
                                <FormSection>
                                    {/* Issue with infinite rendering when using component syntax, so calling as JS function. (e.g. <MigrationFromField {...formProps} />) */}
                                    {MigrationFromField(setFieldValue)}
                                    {ProductField(setFieldValue)}
                                    {NumberOfUsersField(setFieldValue)}
                                    {WhatDoYouWantToLearnField(setFieldValue)}
                                    {QuestionField(
                                        "migration-options",
                                        handleBlurEvent
                                    )}
                                </FormSection>
                                {ConsentInfo(setFieldValue)}
                                <FormFooter align={"start"}>
                                    {FooterSection(formProps.onSubmit)}
                                </FormFooter>
                            </form>
                        )}
                    </Form>
                </div>
                <MigrationOptionsResourceCards />
            </div>
        </SelectedProductProvider>
    );

    if (userInfo.isUserLoggedIn && userInfo.email) {
        if (
            isPersonalDomain(userInfo.email) ||
            userInfo.email.includes("atlassian.com") ||
            changeContactInfo
        ) {
            return loggedOutView;
        } else {
            return loggedInView;
        }
    }

    return loggedOutView;

    return (
        <section>
            <div>Migration Options</div>
            <p>{userCountGreaterThan5000Error}</p>
            <p>{companyNameLengthError}</p>
        </section>
    );
};

export default MigrationOptions;
